import { useState, useMemo } from "react";

export type RepaymentFrequency = "weekly" | "fortnightly" | "monthly";

export interface RepaymentInputs {
  loanAmount: number;
  interestRate: number; // Annual percentage
  loanTerm: number; // Years
  frequency: RepaymentFrequency;
}

export interface RepaymentResults {
  repaymentAmount: number;
  totalRepayments: number;
  totalInterest: number;
  numberOfPayments: number;
  effectiveRate: number;
}

export interface AmortizationRow {
  period: number;
  payment: number;
  principal: number;
  interest: number;
  balance: number;
}

const defaultInputs: RepaymentInputs = {
  loanAmount: 500000,
  interestRate: 6.0,
  loanTerm: 30,
  frequency: "monthly",
};

/**
 * Calculate repayment using PMT formula
 * M = P * [r(1+r)^n] / [(1+r)^n - 1]
 */
function calculateRepayment(
  principal: number,
  annualRate: number,
  years: number,
  frequency: RepaymentFrequency
): { payment: number; periods: number; periodRate: number } {
  const periodsPerYear = frequency === "weekly" ? 52 : frequency === "fortnightly" ? 26 : 12;
  const periods = years * periodsPerYear;
  const periodRate = annualRate / 100 / periodsPerYear;

  if (periodRate === 0) {
    return { payment: principal / periods, periods, periodRate };
  }

  const payment =
    (principal * (periodRate * Math.pow(1 + periodRate, periods))) /
    (Math.pow(1 + periodRate, periods) - 1);

  return { payment, periods, periodRate };
}

/**
 * Generate amortization schedule (yearly summary)
 */
function generateAmortizationSchedule(
  principal: number,
  annualRate: number,
  years: number,
  frequency: RepaymentFrequency
): AmortizationRow[] {
  const { payment, periodRate } = calculateRepayment(principal, annualRate, years, frequency);
  const periodsPerYear = frequency === "weekly" ? 52 : frequency === "fortnightly" ? 26 : 12;

  const schedule: AmortizationRow[] = [];
  let balance = principal;

  for (let year = 1; year <= years; year++) {
    let yearlyPrincipal = 0;
    let yearlyInterest = 0;

    for (let period = 0; period < periodsPerYear; period++) {
      if (balance <= 0) break;

      const interestPayment = balance * periodRate;
      const principalPayment = Math.min(payment - interestPayment, balance);

      yearlyInterest += interestPayment;
      yearlyPrincipal += principalPayment;
      balance -= principalPayment;
    }

    schedule.push({
      period: year,
      payment: payment * periodsPerYear,
      principal: yearlyPrincipal,
      interest: yearlyInterest,
      balance: Math.max(0, balance),
    });

    if (balance <= 0) break;
  }

  return schedule;
}

/**
 * Repayment calculator hook
 */
export function useRepaymentCalculator() {
  const [inputs, setInputs] = useState<RepaymentInputs>(defaultInputs);

  const results = useMemo<RepaymentResults>(() => {
    const { payment, periods, periodRate } = calculateRepayment(
      inputs.loanAmount,
      inputs.interestRate,
      inputs.loanTerm,
      inputs.frequency
    );

    const totalRepayments = payment * periods;
    const totalInterest = totalRepayments - inputs.loanAmount;

    return {
      repaymentAmount: Math.round(payment * 100) / 100,
      totalRepayments: Math.round(totalRepayments * 100) / 100,
      totalInterest: Math.round(totalInterest * 100) / 100,
      numberOfPayments: periods,
      effectiveRate: periodRate * 100,
    };
  }, [inputs]);

  const amortization = useMemo(() => {
    return generateAmortizationSchedule(
      inputs.loanAmount,
      inputs.interestRate,
      inputs.loanTerm,
      inputs.frequency
    );
  }, [inputs]);

  const updateInput = <K extends keyof RepaymentInputs>(key: K, value: RepaymentInputs[K]) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };

  return {
    inputs,
    results,
    amortization,
    updateInput,
    setInputs,
  };
}
